import jwtDecode from 'jwt-decode'

/**
 * Decodifica un JWT token y retorna el payload
 * @param {string} token - JWT token
 * @returns {object|null} Payload del token o null si es inválido
 */
export const decodeToken = token => {
  try {
    return jwtDecode(token)
  } catch (error) {
    console.error('❌ Error decodificando token:', error)
    return null
  }
}

/**
 * Verifica si un token está expirado
 * @param {string} token - JWT token
 * @returns {boolean} true si está expirado, false si no
 */
export const isTokenExpired = token => {
  if (!token) return true

  const decoded = decodeToken(token)
  if (!decoded || !decoded.exp) return true

  const currentTime = Date.now() / 1000
  const isExpired = decoded.exp < currentTime

  if (isExpired) {
    console.log('⏰ Token expirado:', {
      exp: new Date(decoded.exp * 1000),
      now: new Date()
    })
  }

  return isExpired
}

/**
 * Verifica si un token está próximo a expirar (dentro de los próximos 5 minutos)
 * @param {string} token - JWT token
 * @param {number} threshold - Umbral en segundos (default: 300 = 5 minutos)
 * @returns {boolean} true si está próximo a expirar
 */
export const isTokenExpiringSoon = (token, threshold = 300) => {
  if (!token) return true

  const decoded = decodeToken(token)
  if (!decoded || !decoded.exp) return true

  const currentTime = Date.now() / 1000
  const timeUntilExpiry = decoded.exp - currentTime

  const expiringSoon = timeUntilExpiry < threshold

  if (expiringSoon && timeUntilExpiry > 0) {
    console.log('⚠️ Token expirará pronto:', {
      timeLeft: `${Math.floor(timeUntilExpiry / 60)} minutos`,
      exp: new Date(decoded.exp * 1000)
    })
  }

  return expiringSoon
}

/**
 * Obtiene el tiempo restante hasta la expiración del token en segundos
 * @param {string} token - JWT token
 * @returns {number} Segundos hasta expiración (0 si ya expiró)
 */
export const getTokenTimeLeft = token => {
  if (!token) return 0

  const decoded = decodeToken(token)
  if (!decoded || !decoded.exp) return 0

  const currentTime = Date.now() / 1000
  const timeLeft = Math.max(0, decoded.exp - currentTime)

  return timeLeft
}

/**
 * Obtiene información del token
 * @param {string} token - JWT token
 * @returns {object} Información del token
 */
export const getTokenInfo = token => {
  if (!token) {
    return {
      valid: false,
      expired: true,
      payload: null,
      expiresAt: null,
      timeLeft: 0
    }
  }

  const decoded = decodeToken(token)
  const expired = isTokenExpired(token)
  const timeLeft = getTokenTimeLeft(token)

  return {
    valid: !!decoded,
    expired,
    payload: decoded,
    expiresAt: decoded?.exp ? new Date(decoded.exp * 1000) : null,
    timeLeft,
    timeLeftFormatted: formatTimeLeft(timeLeft)
  }
}

/**
 * Formatea el tiempo restante en un formato legible
 * @param {number} seconds - Segundos
 * @returns {string} Tiempo formateado
 */
const formatTimeLeft = seconds => {
  if (seconds <= 0) return 'Expirado'

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)

  if (hours > 0) {
    return `${hours}h ${minutes}m`
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`
  } else {
    return `${secs}s`
  }
}

/**
 * Obtiene los tokens desde localStorage
 * @returns {object} { accessToken, refreshToken }
 */
export const getTokensFromStorage = () => {
  return {
    accessToken: localStorage.getItem('accessToken'),
    refreshToken: localStorage.getItem('refreshToken')
  }
}

/**
 * Verifica si necesita renovar el access token
 * @returns {boolean} true si necesita renovación
 */
export const shouldRefreshToken = () => {
  const { accessToken, refreshToken } = getTokensFromStorage()

  // Si no hay refresh token, no podemos renovar
  if (!refreshToken) {
    console.log('⚠️ No hay refresh token disponible')
    return false
  }

  // Si el refresh token está expirado, no podemos renovar
  if (isTokenExpired(refreshToken)) {
    console.log('❌ Refresh token expirado')
    return false
  }

  // Si el access token está expirado o próximo a expirar, renovar
  if (
    !accessToken ||
    isTokenExpired(accessToken) ||
    isTokenExpiringSoon(accessToken)
  ) {
    console.log('🔄 Access token necesita renovación')
    return true
  }

  return false
}

/**
 * Logs información de tokens para debugging
 */
export const logTokensInfo = () => {
  const { accessToken, refreshToken } = getTokensFromStorage()

  console.group('🔑 Token Information')

  console.log('Access Token:', getTokenInfo(accessToken))
  console.log('Refresh Token:', getTokenInfo(refreshToken))

  console.groupEnd()
}
